'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { APIStatus } from '@application/enums/api-status';
import { RelationshipTypeEnum } from '@application/enums/relationship-type-enum';
import { clearFamilyRelation } from '@application/features/familyRelations/familyRelationsSlice';
import { createFamilyRelationAsync, getFamilyRelationByIdAsync, updateFamilyRelationAsync } from '@application/features/familyRelations/familyRelationsThunk';
import { getPersonByIdAsync } from '@application/features/people/peopleThunk';
import { isZeroDate } from '@application/utilities/date-utils';
import { useAppDispatch, useAppSelector } from '@application/store/useStore';
import { Alert, Box, Button, CircularProgress, Typography, useMediaQuery, useTheme } from '@mui/material';
import BDrawer from '@ui/components/common/BDrawer';
import CustomDialog from '@ui/components/common/Dialog';
import HSingleDatePicker from '@ui/components/common/Pickers/HDatePicker';
import { ControlledFilledTextField } from '@ui/components/common/TextField';
import SingleSelect from '@ui/components/common/Autocomplete/SingleSelect';
import ImageUpload from '@ui/pages/Users/AddEditUser/components/ImageUpload';
import { useEffect, useRef, useMemo } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
const AddEditFamilyMemberDialog = ({ open, onClose, title, edit, personId, familyRelationId, familyMembers = [], onSuccess }) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isBelow1280 = useMediaQuery(theme.breakpoints.down(1280));
    const formRef = useRef(null);
    const dispatch = useAppDispatch();
    // Redux state
    const { familyRelation, status, apiError } = useAppSelector((state) => state.familyRelations);
    const { control, handleSubmit, formState: { errors }, reset } = useForm({
        defaultValues: {
            profilePicture: null,
            name: '',
            relationship: '',
            dateOfBirth: null,
            dateSincePartner: null
        },
        mode: 'onChange' // Enable real-time validation
    });
    // Watch relationship to show/hide dateSincePartner field
    const selectedRelationship = useWatch({
        control,
        name: 'relationship'
    });
    // Check if a Partner relationship already exists (excluding the current one being edited)
    const hasExistingPartner = useMemo(() => {
        return familyMembers.some((member) => (member.relationship === RelationshipTypeEnum.Partner ||
            member.relationship === 'Partner') &&
            member.id !== familyRelationId // Exclude the current member if editing
        );
    }, [familyMembers, familyRelationId]);
    // Relationship options for dropdown
    const relationshipOptions = useMemo(() => {
        // Check if we're editing the existing Partner
        const isEditingExistingPartner = edit && familyRelation?.relationship === RelationshipTypeEnum.Partner;
        return Object.values(RelationshipTypeEnum)
            .filter((value) => {
            // If Partner already exists:
            // - Allow it if we're editing the existing Partner
            // - Filter it out if we're creating new or editing a different member
            if (value === RelationshipTypeEnum.Partner && hasExistingPartner) {
                return isEditingExistingPartner;
            }
            return true;
        })
            .map((value) => {
            const translationKey = `familyRelation.relationship.${value}`;
            const translatedLabel = t(translationKey);
            // If translation returns the key itself (not found), use the enum value as fallback
            const label = translatedLabel !== translationKey ? translatedLabel : value;
            return {
                value,
                label
            };
        });
    }, [t, hasExistingPartner, edit, familyRelation]);
    // Load family relation data for edit mode
    useEffect(() => {
        if (edit && familyRelationId && open) {
            dispatch(getFamilyRelationByIdAsync(familyRelationId));
        }
    }, [edit, familyRelationId, open, dispatch]);
    // Populate form when family relation data is loaded
    useEffect(() => {
        if (familyRelation && edit) {
            // Helper to safely parse date
            const parseDate = (dateValue) => {
                if (!dateValue || isZeroDate(dateValue))
                    return null;
                try {
                    const parsed = new Date(dateValue);
                    return !isNaN(parsed.getTime()) ? parsed : null;
                }
                catch {
                    return null;
                }
            };
            reset({
                profilePicture: familyRelation.fileUrl || null,
                name: familyRelation.name,
                relationship: familyRelation.relationship,
                dateOfBirth: parseDate(familyRelation.dateOfBirth),
                dateSincePartner: parseDate(familyRelation.dateSincePartner)
            });
        }
    }, [familyRelation, edit, reset]);
    // Clear family relation data when dialog closes or reset form when opening in create mode
    useEffect(() => {
        if (!open) {
            dispatch(clearFamilyRelation());
            // Reset form to default values when dialog closes
            reset({
                profilePicture: null,
                name: '',
                relationship: '',
                dateOfBirth: null,
                dateSincePartner: null
            });
        }
        else if (open && !edit) {
            // Reset form when opening in create mode
            reset({
                profilePicture: null,
                name: '',
                relationship: '',
                dateOfBirth: null,
                dateSincePartner: null
            });
        }
    }, [open, edit, dispatch, reset]);
    const onSubmit = async (data) => {
        try {
            if (edit && familyRelationId) {
                // Update existing family relation
                const updateData = {
                    id: familyRelationId,
                    personId: personId,
                    name: data.name,
                    dateOfBirth: data.dateOfBirth,
                    relationship: data.relationship,
                    dateSincePartner: data.dateSincePartner
                        ? data.dateSincePartner instanceof Date
                            ? data.dateSincePartner
                            : new Date(data.dateSincePartner)
                        : undefined,
                    // Only include picture if it's a File (new upload), not if it's a string (existing URL)
                    picture: data.profilePicture instanceof File ? data.profilePicture : undefined
                };
                await dispatch(updateFamilyRelationAsync(updateData)).unwrap();
                if (personId) {
                    dispatch(getPersonByIdAsync(personId));
                }
                // Show success toast
                toast.success(t('people.familyMemberUpdatedSuccessfully'));
            }
            else {
                // Create new family relation
                const createData = {
                    personId: personId,
                    name: data.name,
                    dateOfBirth: data.dateOfBirth,
                    relationship: data.relationship,
                    dateSincePartner: data.dateSincePartner
                        ? data.dateSincePartner instanceof Date
                            ? data.dateSincePartner
                            : new Date(data.dateSincePartner)
                        : undefined,
                    picture: data.profilePicture instanceof File ? data.profilePicture : undefined
                };
                await dispatch(createFamilyRelationAsync(createData)).unwrap();
                if (personId) {
                    dispatch(getPersonByIdAsync(personId));
                }
                // Show success toast
                toast.success(t('people.familyMemberCreatedSuccessfully'));
            }
            // Success - close dialog and call success callback
            onSuccess?.();
            onClose();
        }
        catch (error) {
            console.error('Error saving family member:', error);
            // Show error toast
            const errorMessage = edit
                ? t('people.failedToUpdateFamilyMember')
                : t('people.failedToCreateFamilyMember');
            toast.error(errorMessage);
        }
    };
    const formContent = (_jsxs(Box, { component: 'form', ref: formRef, onSubmit: handleSubmit(onSubmit), sx: {
            p: isBelow1280 ? 0 : 3,
            gap: 1.5,
            display: 'flex',
            flexDirection: 'column',
            height: '100%'
        }, children: [apiError && (_jsx(Alert, { severity: 'error', sx: { mb: 2 }, children: apiError.error || 'An error occurred while saving the family member.' })), status === APIStatus.PENDING && (_jsx(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', py: 2, children: _jsx(CircularProgress, { size: 24 }) })), _jsx(Box, { display: 'flex', justifyContent: 'center', alignItems: 'center', flexDirection: 'column', children: _jsx(Controller, { control: control, name: 'profilePicture', rules: {
                        validate: (value) => {
                            if (!value)
                                return true; // Optional field
                            // If it's a File (new upload), validate it
                            if (value instanceof File) {
                                // Check file type
                                const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
                                if (!allowedTypes.includes(value.type)) {
                                    return t('validations.invalidFileType', {
                                        field: t('profile.picture'),
                                        types: 'JPEG, PNG, JPG'
                                    });
                                }
                                // Check file size (5MB max)
                                const maxSize = 5 * 1024 * 1024; // 5MB in bytes
                                if (value.size > maxSize) {
                                    return t('validations.maxFileSize', {
                                        field: t('profile.picture'),
                                        max: '5MB'
                                    });
                                }
                            }
                            // If it's a string (existing URL), it's valid
                            if (typeof value === 'string') {
                                return true;
                            }
                            return true;
                        }
                    }, render: ({ field }) => (_jsx(ImageUpload, { control: control, name: 'profilePicture', edit: edit, types: ['JPG', 'JPEG', 'PNG'] })) }) }), _jsx(ControlledFilledTextField, { bg: 'grey8', control: control, name: 'name', label: t('events.eventDetails.invitationsTab.name'), errorMessage: errors.name?.message, fullWidth: true, margin: 'normal', rules: {
                    required: t('validations.required', {
                        field: t('events.eventDetails.invitationsTab.name')
                    }),
                    minLength: {
                        value: 2,
                        message: t('validations.minLength', {
                            field: t('events.eventDetails.invitationsTab.name'),
                            min: 2
                        })
                    },
                    maxLength: {
                        value: 50,
                        message: t('validations.maxLength', {
                            field: t('events.eventDetails.invitationsTab.name'),
                            max: 50
                        })
                    },
                    pattern: {
                        value: /^[a-zA-Z\s'-]+$/,
                        message: t('validations.onlyLetters', {
                            field: t('events.eventDetails.invitationsTab.name')
                        })
                    }
                } }), _jsx(SingleSelect, { bg: 'grey8', control: control, name: 'relationship', label: t('people.relationship'), options: relationshipOptions, error: errors.relationship?.message, rules: {
                    required: t('validations.required', { field: t('people.relationship') }),
                    validate: (value) => {
                        // Prevent selecting Partner if one already exists and we're not editing that Partner
                        if (value === RelationshipTypeEnum.Partner &&
                            hasExistingPartner &&
                            !(edit && familyRelation?.relationship === RelationshipTypeEnum.Partner)) {
                            return (t('familyRelation.partnerAlreadyExists') ||
                                'A Partner relationship already exists for this person.');
                        }
                        return true;
                    }
                } }), selectedRelationship === RelationshipTypeEnum.Partner && (_jsx(Controller, { control: control, name: 'dateSincePartner', rules: {
                    required: t('familyRelation.dateSincePartnerRequired'),
                    validate: (value) => {
                        if (!value) {
                            return t('familyRelation.dateSincePartnerRequired');
                        }
                        // value is a Date object from the form
                        const partnerDate = value instanceof Date ? value : new Date(value);
                        if (isNaN(partnerDate.getTime())) {
                            return t('validations.invalidDate', {
                                field: t('familyRelation.dateSincePartner')
                            });
                        }
                        const today = new Date();
                        if (partnerDate > today) {
                            return t('validations.futureDate', {
                                field: t('familyRelation.dateSincePartner')
                            });
                        }
                        return true;
                    }
                }, render: ({ field }) => {
                    const getDateValue = () => {
                        if (!field.value)
                            return undefined;
                        if (field.value instanceof Date && !isNaN(field.value.getTime())) {
                            return field.value.toISOString().split('T')[0];
                        }
                        if (typeof field.value === 'string') {
                            const parsedDate = new Date(field.value);
                            if (!isNaN(parsedDate.getTime())) {
                                return parsedDate.toISOString().split('T')[0];
                            }
                        }
                        return undefined;
                    };
                    return (_jsxs(Box, { children: [_jsx(HSingleDatePicker, { value: getDateValue(), bg: 'grey8', handleSelectedDate: (date) => field.onChange(date ? new Date(date) : null), label: t('familyRelation.dateSincePartner'), minDate: new Date('1900-01-01'), maxDate: new Date() }), errors.dateSincePartner && (_jsx(Typography, { variant: 'caption', color: 'error', sx: { mt: 0.5, display: 'block' }, children: errors.dateSincePartner.message }))] }));
                } })), _jsx(Controller, { control: control, name: 'dateOfBirth', rules: {
                    required: t('validations.required', { field: t('family.dateOfBirth') }),
                    validate: (value) => {
                        if (!value)
                            return t('validations.required', { field: t('family.dateOfBirth') });
                        const birthDate = new Date(value);
                        if (isNaN(birthDate.getTime())) {
                            return t('validations.invalidDate', { field: t('family.dateOfBirth') });
                        }
                        const today = new Date();
                        const age = today.getFullYear() - birthDate.getFullYear();
                        if (birthDate > today) {
                            return t('validations.futureDate', { field: t('family.dateOfBirth') });
                        }
                        if (age > 150) {
                            return t('validations.maxAge', {
                                field: t('family.dateOfBirth'),
                                max: 150
                            });
                        }
                        if (age < 0) {
                            return t('validations.invalidDate', { field: t('family.dateOfBirth') });
                        }
                        return true;
                    }
                }, render: ({ field }) => {
                    const getDateValue = () => {
                        if (!field.value)
                            return undefined;
                        if (field.value instanceof Date && !isNaN(field.value.getTime())) {
                            return field.value.toISOString().split('T')[0];
                        }
                        if (typeof field.value === 'string') {
                            const parsedDate = new Date(field.value);
                            if (!isNaN(parsedDate.getTime())) {
                                return parsedDate.toISOString().split('T')[0];
                            }
                        }
                        return undefined;
                    };
                    return (_jsx(HSingleDatePicker, { value: getDateValue(), bg: 'grey8', handleSelectedDate: (date) => field.onChange(date ? new Date(date) : null), label: t('family.dateOfBirth'), minDate: new Date('1900-01-01'), maxDate: new Date() }));
                } }), errors.dateOfBirth && (_jsx(Typography, { variant: 'caption', color: 'error', sx: { mt: -1, mb: 1 }, children: errors.dateOfBirth.message })), _jsxs(Box, { mt: 'auto', display: 'flex', gap: 2, children: [_jsx(Button, { fullWidth: true, onClick: onClose, variant: 'outlined', color: 'inherit', size: 'large', children: t('actions.cancel') }), _jsx(Button, { fullWidth: true, variant: 'contained', color: 'inherit', size: 'large', disabled: status === APIStatus.PENDING, type: 'submit', children: status === APIStatus.PENDING ? (_jsx(CircularProgress, { size: 20, color: 'inherit' })) : edit ? (t('actions.editNow')) : (t('actions.addNow')) })] })] }));
    return isBelow1280 ? (_jsx(BDrawer, { open: open, onClose: onClose, title: title, children: formContent })) : (_jsx(CustomDialog, { backgroundVariant: 'white', open: open, onClose: onClose, titleWithIcon: title, titleIcon: '/assets/images/FamilyMember.png', maxWidth: 'sm', actions: null, children: formContent }));
};
export default AddEditFamilyMemberDialog;
